<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.interfaces.pageExchangeRates.title') }}
    </portal>
    <template v-if="config !== null">
      <card :title="$t('admin.interfaces.pageExchangeRates.card.general')" cols="12 6@desktop" rows="1">
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageExchangeRates.enabled')"
                           :disabled="!hasRole('ROLE_SYSADMIN')"
                           :enabled-title="$t('enabled')"
                           :disabled-title="$t('disabled')"
                           v-model="config.enabled"
                           cols="12 6@hd start-1"
                           name="enabled" />
          <hm-input-money :label="$t('admin.interfaces.pageExchangeRates.markup')"
                          :help="$t('admin.interfaces.pageExchangeRates.markup_help')"
                          :digits="2"
                          :required="true"
                          v-model="config.markup"
                          currency="%"
                          cols="12 6@hd start-1"
                          name="markup" />
          <hm-input-money :label="$t('admin.interfaces.pageExchangeRates.exchangeFee')"
                          :required="true"
                          v-model="config.exchangeFee"
                          currency="%"
                          cols="12 6@hd start-1"
                          name="exchangeFee" />
          <hm-input-money :label="$t('admin.interfaces.pageExchangeRates.minimumExchangeFee')"
                          :required="true"
                          :currency="hotelSettings.details.minimumExchangeFee"
                          v-model="config.minimumExchangeFee"
                          cols="12 6@hd start-1"
                          name="minimumExchangeFee" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.interfaces.pageExchangeRates.card.calculator')" cols="6@desktop start-1" rows="1">
        <hm-input-number v-model="amount"
                         :label="$t('admin.interfaces.pageExchangeRates.amount')"
                         :required="true"
                         class="text-right"
                         currency="EUR"
                         cols="12 6@hd start-1"
                         name="amount" />
        <hm-input-select v-model="from"
                         :label="$t('admin.interfaces.pageExchangeRates.from')"
                         cols="3 start-1"
                         rows="1"
                         name="from">
          <option :value="hotelSettings.details.currency" v-html="hotelSettings.details.currency"></option>
          <option v-for="currency in config.supportedCurrencies" :value="currency" v-html="currency"></option>
        </hm-input-select>
        <hm-input-select v-model="to"
                         :label="$t('admin.interfaces.pageExchangeRates.to')"
                         cols="3"
                         rows="1"
                         name="to">
          <option :value="hotelSettings.details.currency" v-html="hotelSettings.details.currency"></option>
          <option v-for="currency in config.supportedCurrencies" :value="currency" v-html="currency"></option>
        </hm-input-select>

        <hm-input-number v-model="result.result"
                         :label="$t('admin.interfaces.pageExchangeRates.result')"
                         :disabled="true"
                         class="text-right"
                         currency="EUR"
                         cols="12 6@hd start-1"
                         name="result" />

        <hm-col v-if="hasRole('ROLE_SYSADMIN')" cols="12">
          <pre v-html="result" />
        </hm-col>
      </card>
      <card v-if="!isEmpty(rates)" :title="$t('admin.interfaces.pageExchangeRates.card.exchangeRates')" cols="12 6@desktop start-7@desktop" rows="3 start-1@desktop">
        <template slot="header-right">
          {{ rates.lastModified | toDateTime }} UTC
        </template>
        <hm-card-inner>
          <hm-col cols="12">
            <div style="position:relative; margin-bottom: 0.8rem;">
              {{ $t('admin.interfaces.pageExchangeRates.for1youGet', [1, hotelSettings.details.currency]) }}
            </div>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner :full-size="true">
          <hm-table :sticky="true"
                    columns="5"
                    grid-template-columns="16rem 16rem 16rem 16rem minmax(6rem, 1fr)"
                    min-width="70rem">
            <template slot="table-head">
              <div class="hm-table__th">{{ $t('admin.interfaces.pageExchangeRates.rates.currency') }}</div>
              <div class="hm-table__th text-right">{{ $t('admin.interfaces.pageExchangeRates.rates.rateSell') }}</div>
              <div class="hm-table__th text-right">{{ $t('admin.interfaces.pageExchangeRates.rates.rate') }}</div>
              <div class="hm-table__th text-right">{{ $t('admin.interfaces.pageExchangeRates.rates.rateBuy') }}</div>
              <div class="hm-table__th text-right">&nbsp;</div>
            </template>
            <template slot="default">
              <template v-for="rate in rates.rates">
                <div class="hm-table__td" @click="toggleCurrency(rate.currency)">{{ rate.currency }}</div>
                <div class="hm-table__td text-right" @click="toggleCurrency(rate.currency)">{{ rate.rate * (1 - config.markup/100) | toExchangeRate }}</div>
                <div class="hm-table__td text-right" @click="toggleCurrency(rate.currency)">{{ rate.rate | toExchangeRate }}</div>
                <div class="hm-table__td text-right" @click="toggleCurrency(rate.currency)">{{ rate.rate * (1 + config.markup/100) | toExchangeRate }}</div>
                <div class="hm-table__td hm-table__td--icon text-right" @click="toggleCurrency(rate.currency)"><icon v-if="config.supportedCurrencies.includes(rate.currency)" icon="save" /></div>
              </template>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, first, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';
import HmInputNumber from '../../../lib/inputs/HmInputNumber';
import TimerMixin from '../../../../mixins/TimerMixin';

export default {
  name: 'PageExchangeRates',
  components: { HmInputNumber, HmCardInner, HmInputMoney, HmInputCheckbox, Card },

  filters: {
    toExchangeRate: function (value) {
      return `${(isNil(value) ? 0 : value).toLocaleString(
        window.i18n.locale,
        {
          minimumFractionDigits: 6,
          maximumFractionDigits: 6,
        },
      )}`;
    },
  },

  mixins: [HotelmeisterMixin, SaveWatchMixin, TimerMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      fn: function (cbSaveComplete) {
        this.axios.put(this.serviceUrls.interfaces.exchangeRate.get(this, this.config.id), this.config)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
      rates: null,
      amount: 1000.0,
      from: 'EUR',
      to: 'EUR',
      result: { result: 0.0 },
    };
  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
  },

  watch: {
    config: {
      handler: function () {
        this.setTimeout(() => {
          this.calculate();
        }, 1500);
      },
      deep: true,
    },
    amount: function () {
      this.calculate();
    },
    to: function (newVal, oldVal) {
      if (newVal !== this.hotelSettings.details.currency) {
        this.from = this.hotelSettings.details.currency;
      } else {
        this.from = oldVal;
      }
      this.calculate();
    },
    from: function (newVal, oldVal) {
      if (newVal !== this.hotelSettings.details.currency) {
        this.to = this.hotelSettings.details.currency;
      } else {
        this.to = oldVal;
      }
      this.calculate();
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    calculate() {
      this.axios.get(this.serviceUrls.interfaces.exchangeRate.convert(this, this.amount, this.from, this.to))
        .then((response) => {
          this.result = response.data.data;
        });
    },
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.exchangeRate.get(this, null))
        .then((response) => {
          if (isNil(this.config)) {
            this.config = response.data.data;

            this.from = this.hotelSettings.details.currency;
            this.to = first(this.config.supportedCurrencies);

            if (isEmpty(this.to)) {
              this.to = this.hotelSettings.details.currency;
            }
          }
        });

      this.axios.get(this.serviceUrls.interfaces.exchangeRate.getRates(this))
        .then((response) => {
          const rates = response.data.data;
          rates.rates = Object.entries(rates.rates)
            .map(it => ({
              currency: it[0],
              rate: it[1],
            }));

          rates.rates = rates.rates.filter(it => it.currency !== this.hotelSettings.details.currency);

          rates.rates = sortBy(rates.rates, (it) => {
            switch (it.currency) {
              case 'USD':
              case 'EUR':
              case 'CHF':
              case 'CZK':
              case 'HUF':
              case 'AED':
              case 'NOK':
              case 'SEK':
              case 'AUD':
              case 'CAD':
              case 'JPY':
              case 'DKK':
              case 'CRC':
              case 'ISK':
              case 'MXN':
              case 'CNY':
              case 'PLN':
              case 'RUB':
              case 'TRY':
              case 'BRL':
              case 'ZAR':
                return it.currency;
              default:
                return `ZZZ-${it.currency}`;
            }
          });
          this.rates = rates;
        });
    },
    toggleCurrency(currency) {
      if (this.config.supportedCurrencies.includes(currency)) {
        this.config.supportedCurrencies.splice(this.config.supportedCurrencies.indexOf(currency));
      } else {
        this.config.supportedCurrencies.push(currency);
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
@import '~styles/import';

.hm-table__td {
  &--selected {
    &:before {
      content: ' ';
      left: 0;
      right: 0;
      background: $lightgray;
      height: 4.8rem;
      position: absolute;
      z-index: -1;
    }
  }
  &--icon {
    display: flex;
    flex-direction: column;

    .hm-icon {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    @include icon-color($mint);
  }
}
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageExchangeRates.vue